import { StoredSection } from '@/entities/templates';

import { OptionTarget } from '../config';

type RemapFocusedOptionArgs = {
  prevSections: StoredSection[];
  nextSections: StoredSection[];
  focused: OptionTarget | null;
};

// Deletions and reorders keep the surviving objects, while an edit replaces
// the edited object in place, so identity tells a move from an edit.
const relocate = <T>(prev: T[], next: T[], item: T, index: number): number => {
  const movedIndex = next.indexOf(item);

  if (movedIndex >= 0) {
    return movedIndex;
  }

  return next.length < prev.length ? -1 : index;
};

export const remapFocusedOption = ({
  prevSections,
  nextSections,
  focused,
}: RemapFocusedOptionArgs): OptionTarget | null => {
  if (!focused) {
    return null;
  }

  const prevSection = prevSections[focused.sectionIndex];
  if (!prevSection) {
    return null;
  }

  const sectionIndex = relocate(
    prevSections,
    nextSections,
    prevSection,
    focused.sectionIndex,
  );
  if (sectionIndex < 0) {
    return null;
  }

  const prevOptions = prevSection.options ?? [];
  const nextOptions = nextSections[sectionIndex]?.options ?? [];
  const prevOption = prevOptions[focused.optionIndex];
  if (!prevOption) {
    return null;
  }

  const optionIndex = relocate(
    prevOptions,
    nextOptions,
    prevOption,
    focused.optionIndex,
  );

  return optionIndex < 0 ? null : { sectionIndex, optionIndex };
};
